import produce from 'immer';
import {action as createAction, ActionType} from 'typesafe-actions';

import {actionTypes as system, InitAction} from '../../System';

import * as selectors from './selectors';

// Not moving it to @neos-project/neos-ts-interfaces yet, as it's rather an implementation detail
export interface Formatting {
    [propName: string]: boolean;
}

export interface State extends Readonly<{
    src: string | null;
    previewUrl: string | null;
    formattingUnderCursor: Formatting;
    currentlyEditedPropertyName: string | null;
    isLoading: boolean;
    focusedProperty: string | null;
    backgroundColor: string;
    shouldScrollIntoView: boolean;
    isLinkEditorOpen: boolean;
}> {}

export const defaultState: State = {
    src: null,
    previewUrl: null,
    formattingUnderCursor: {},
    currentlyEditedPropertyName: null,
    isLoading: true,
    focusedProperty: null,
    backgroundColor: '#ffffff',
    shouldScrollIntoView: false,
    isLinkEditorOpen: false
};

//
// Export the action types
//
export enum actionTypes {
    SET_PREVIEW_URL = '@neos/neos-ui/UI/ContentCanvas/SET_PREVIEW_URL',
    SET_SRC = '@neos/neos-ui/UI/ContentCanvas/SET_SRC',
    FORMATTING_UNDER_CURSOR = '@neos/neos-ui/UI/ContentCanvas/FORMATTING_UNDER_CURSOR',
    SET_CURRENTLY_EDITED_PROPERTY_NAME = '@neos/neos-ui/UI/ContentCanvas/SET_CURRENTLY_EDITED_PROPERTY_NAME',
    START_LOADING = '@neos/neos-ui/UI/ContentCanvas/START_LOADING',
    STOP_LOADING = '@neos/neos-ui/UI/ContentCanvas/STOP_LOADING',
    RELOAD = '@neos/neos-ui/UI/ContentCanvas/RELOAD',
    FOCUS_PROPERTY = '@neos/neos-ui/UI/ContentCanvas/FOCUS_PROPERTY',
    REQUEST_SCROLL_INTO_VIEW = '@neos/neos-ui/UI/ContentCanvas/REQUEST_SCROLL_INTO_VIEW',
    REQUEST_REGAIN_CONTROL = '@neos/neos-ui/UI/ContentCanvas/REQUEST_REGAIN_CONTROL',
    REQUEST_LOGIN = '@neos/neos-ui/UI/ContentCanvas/REQUEST_LOGIN',
    TOGGLE_LINK_EDITOR = '@neos/neos-ui/UI/ContentCanvas/TOGGLE_LINK_EDITOR'
}

const setPreviewUrl = (previewUrl: string) => createAction(actionTypes.SET_PREVIEW_URL, previewUrl);
const setSrc = (src: string, metaKeyPressed: boolean = false) => createAction(actionTypes.SET_SRC, {src, metaKeyPressed});
const setFormattingUnderCursor = (formatting: Formatting) => createAction(actionTypes.FORMATTING_UNDER_CURSOR, formatting);
const setCurrentlyEditedPropertyName = (propertyName: string) => createAction(actionTypes.SET_CURRENTLY_EDITED_PROPERTY_NAME, propertyName);
const startLoading = () => createAction(actionTypes.START_LOADING);
const stopLoading = () => createAction(actionTypes.STOP_LOADING);
const reload = (uri: string) => createAction(actionTypes.RELOAD, {uri});
// Set a flag to tell ContentCanvas to scroll the focused node into view
const requestScrollIntoView = (activate: boolean) => createAction(actionTypes.REQUEST_SCROLL_INTO_VIEW, activate);
// If we have lost controll over the iframe, we need to take action
const requestRegainControl = (src: string, errorMessage: string) => createAction(actionTypes.REQUEST_REGAIN_CONTROL, {src, errorMessage});
const requestLogin = () => createAction(actionTypes.REQUEST_LOGIN);
const toggleLinkEditor = (forceState?: boolean) => createAction(actionTypes.TOGGLE_LINK_EDITOR, forceState);

//
// Export the actions
//
export const actions = {
    setPreviewUrl,
    setSrc,
    setFormattingUnderCursor,
    setCurrentlyEditedPropertyName,
    startLoading,
    stopLoading,
    reload,
    requestScrollIntoView,
    requestRegainControl,
    requestLogin,
    toggleLinkEditor
};

export type Action = ActionType<typeof actions>;

//
// Export the reducer
//
export const reducer = (state: State = defaultState, action: InitAction | Action) => produce(state, draft => {
    switch (action.type) {
        case system.INIT: {
            draft.backgroundColor = action?.payload?.ui?.contentCanvas?.backgroundColor;
            draft.src = action?.payload?.ui?.contentCanvas?.src || null;
            break;
        }
        case actionTypes.SET_PREVIEW_URL: {
            draft.previewUrl = action.payload;
            break;
        }
        case actionTypes.SET_SRC: {
            draft.src = action.payload.src;
            break;
        }
        case actionTypes.FORMATTING_UNDER_CURSOR: {
            draft.formattingUnderCursor = action.payload;
            // if new selection doesn't have a link, close the link dialog
            draft.isLinkEditorOpen = Boolean(action.payload?.link);
            break;
        }
        case actionTypes.SET_CURRENTLY_EDITED_PROPERTY_NAME: {
            draft.currentlyEditedPropertyName = action.payload;
            draft.isLinkEditorOpen = false;
            break;
        }
        case actionTypes.STOP_LOADING: {
            draft.isLoading = false;
            break;
        }
        case actionTypes.START_LOADING: {
            draft.isLoading = true;
            break;
        }
        case actionTypes.REQUEST_SCROLL_INTO_VIEW: {
            draft.shouldScrollIntoView = action.payload;
            break;
        }
        case actionTypes.REQUEST_REGAIN_CONTROL: {
            draft.src = '';
            break;
        }
        case actionTypes.TOGGLE_LINK_EDITOR: {
            if (action.payload !== undefined) {
                draft.isLinkEditorOpen = action.payload;
            } else {
                draft.isLinkEditorOpen = !state.isLinkEditorOpen;
            }
            break;
        }
    }
});

//
// Export the selectors
//
export {selectors};
